import {
  createClipperRun, jumpClipperRun, setClipperDuck, stepClipperRun, clipperRunScore,
  type ClipperRunState,
} from './clipperRun';

// Versioned with the backend copy: changes to scoring/physics require a new version.
export const RULES_VERSION = 2;
export const RUN_FPS = 60;
export const MAX_RUN_FRAMES = RUN_FPS * 60 * 10;
export const MAX_RUN_EVENTS = 4096;
export type RunAction = 'jump' | 'duck' | 'stand';
export interface RunInputEvent { frame: number; action: RunAction }
export interface RunSimulation {
  state: ClipperRunState;
  frame: number;
  duckHeld: boolean;
  random: () => number;
}

export function createRunSimulation(seed: number): RunSimulation {
  let cursor = seed >>> 0;
  return {
    state: createClipperRun(),
    frame: 0,
    duckHeld: false,
    random: () => {
      cursor ^= cursor << 13;
      cursor ^= cursor >>> 17;
      cursor ^= cursor << 5;
      return (cursor >>> 0) / 4294967296;
    },
  };
}

/** Advances exactly one export-independent game tick. No clocks or DOM access. */
export function tickRunSimulation(simulation: RunSimulation, actions: readonly RunAction[]): void {
  for (const action of actions) {
    if (action === 'jump') simulation.state = jumpClipperRun(simulation.state);
    else simulation.duckHeld = action === 'duck';
  }
  simulation.state = setClipperDuck(simulation.state, simulation.duckHeld);
  simulation.state = stepClipperRun(simulation.state, 1 / RUN_FPS, simulation.random);
  simulation.frame += 1;
}

/** Replays only admitted, bounded inputs; the caller's score is never trusted. */
export function replayClipperRun(seed: number, frames: number, events: readonly RunInputEvent[]) {
  const invalid = () => { throw new Error('invalid_run_recording'); };
  if (!Number.isInteger(seed) || seed <= 0 || seed > 2147483647
    || !Number.isInteger(frames) || frames < 1 || frames > MAX_RUN_FRAMES
    || !Array.isArray(events) || events.length < 1 || events.length > MAX_RUN_EVENTS) invalid();
  if (events[0]?.frame !== 0 || events[0]?.action !== 'jump') invalid();
  let previous = -1;
  for (const event of events) {
    if (!event || !Number.isInteger(event.frame) || event.frame < previous
      || event.frame < 0 || event.frame >= frames
      || !['jump', 'duck', 'stand'].includes(event.action)) invalid();
    previous = event.frame;
  }
  const sim = createRunSimulation(seed);
  let cursor = 0;
  for (let frame = 0; frame < frames; frame += 1) {
    if (sim.state.phase === 'dead') invalid();
    const actions: RunAction[] = [];
    while (cursor < events.length && events[cursor].frame === frame) {
      actions.push(events[cursor].action);
      cursor += 1;
    }
    tickRunSimulation(sim, actions);
  }
  return { score: clipperRunScore(sim.state), frames: sim.frame, phase: sim.state.phase };
}
